import React from 'react';
import {
  Truck, MapPin, Zap, Package, ClipboardList, Edit, Move, FileText, History
} from 'lucide-react';
import { Card } from '../../common/Card';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/I18nProvider';

interface DashboardProps {
  onModuleChange: (moduleId: string) => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ onModuleChange }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // 9 hovedmoduler for lageroperasjoner
  const dashboardModules = [
    { 
      id: 'receipt', 
      name: t('warehouse.modules.receipt.title'), 
      icon: Truck, 
      color: 'purple', 
      description: t('warehouse.modules.receipt.description') 
    },
    { 
      id: 'put-away', 
      name: t('warehouse.modules.putaway.title'), 
      icon: MapPin, 
      color: 'green', 
      description: t('warehouse.modules.putaway.description') 
    },
    { 
      id: 'modern-picking', 
      name: t('warehouse.modules.picking.title'), 
      icon: Zap, 
      color: 'yellow', 
      description: t('warehouse.modules.picking.description') 
    },
    { 
      id: 'packing', 
      name: t('warehouse.modules.packing.title'), 
      icon: Package, 
      color: 'orange', 
      description: t('warehouse.modules.packing.description') 
    },
    { 
      id: 'inventory', 
      name: t('warehouse.modules.inventory.title'), 
      icon: ClipboardList, 
      color: 'indigo', 
      description: t('warehouse.modules.inventory.description') 
    },
    { 
      id: 'correction', 
      name: t('warehouse.modules.inventoryCorrection.title'), 
      icon: Edit, 
      color: 'red', 
      description: t('warehouse.modules.inventoryCorrection.description') 
    },
    { 
      id: 'movement', 
      name: t('warehouse.modules.warehouseTransfer.title'), 
      icon: Move, 
      color: 'cyan', 
      description: t('warehouse.modules.warehouseTransfer.description') 
    },
    { 
      id: 'item-admin', 
      name: t('warehouse.modules.itemAdmin.title'), 
      icon: FileText, 
      color: 'teal', 
      description: t('warehouse.modules.itemAdmin.description') 
    },
    { 
      id: 'history', 
      name: t('warehouse.modules.history.title'), 
      icon: History, 
      color: 'gray', 
      description: t('warehouse.modules.history.description') 
    }
  ];

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: string } = {
      purple: 'bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-900',
      green: 'bg-green-50 hover:bg-green-100 border-green-200 text-green-900',
      yellow: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200 text-yellow-900',
      orange: 'bg-orange-50 hover:bg-orange-100 border-orange-200 text-orange-900',
      indigo: 'bg-indigo-50 hover:bg-indigo-100 border-indigo-200 text-indigo-900',
      red: 'bg-red-50 hover:bg-red-100 border-red-200 text-red-900',
      cyan: 'bg-cyan-50 hover:bg-cyan-100 border-cyan-200 text-cyan-900',
      teal: 'bg-teal-50 hover:bg-teal-100 border-teal-200 text-teal-900',
      gray: 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900'
    };
    return colorMap[color] || 'bg-gray-50 hover:bg-gray-100 border-gray-200 text-gray-900';
  };

  const getIconColorClasses = (color: string) => {
    const iconColorMap: { [key: string]: string } = {
      purple: 'text-purple-600',
      green: 'text-green-600',
      yellow: 'text-yellow-600',
      orange: 'text-orange-600',
      indigo: 'text-indigo-600',
      red: 'text-red-600',
      cyan: 'text-cyan-600',
      teal: 'text-teal-600',
      gray: 'text-gray-600'
    };
    return iconColorMap[color] || 'text-gray-600';
  };

  // Korte navn for mobil - bruker oversettelser
  const getShortName = (moduleId: string) => {
    const shortNameKeys: { [key: string]: string } = {
      'receipt': 'warehouse.modules.receipt.shortName',
      'put-away': 'warehouse.modules.putaway.shortName',
      'modern-picking': 'warehouse.modules.picking.shortName',
      'packing': 'warehouse.modules.packing.shortName',
      'inventory': 'warehouse.modules.inventory.shortName',
      'correction': 'warehouse.modules.inventoryCorrection.shortName',
      'movement': 'warehouse.modules.warehouseTransfer.shortName',
      'item-admin': 'warehouse.modules.itemAdmin.shortName',
      'history': 'warehouse.modules.history.shortName'
    };
    return t(shortNameKeys[moduleId] || '');
  };

  return (
    <div className="relative min-h-screen">
      {/* Main content area - med padding bottom for fast statusbar */}
      <div className="pb-16 sm:pb-20">
        <div className="p-3 sm:p-4 max-w-4xl mx-auto">
          {/* Header område for plukkerelevant informasjon */}
          <div className="mb-4 sm:mb-6">
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-3 sm:p-4 rounded-lg shadow-lg">
              <h2 className="text-base sm:text-xl font-bold mb-1 sm:mb-2 truncate">
                {t('dashboard.welcome', { '0': user?.operatorName || '' })}
              </h2>
              <div className="text-xs sm:text-sm opacity-90">
                <p className="truncate">
                  {t('dashboard.status')}: {t('dashboard.statusReady')} • 
                  {t('dashboard.activePicks')}: 0 • 
                  {t('dashboard.pendingTasks')}: 0
                </p>
              </div>
            </div>
          </div>

          {/* Modulkort - 3 kolonner på mobil, responsivt */}
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
            {dashboardModules.map((module) => {
              const Icon = module.icon;
              return (
                <button
                  key={module.id}
                  onClick={() => onModuleChange(module.id)}
                  className={`${getColorClasses(module.color)} p-2 sm:p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md text-left min-h-[90px] sm:min-h-[120px] flex flex-col active:scale-95 sm:hover:scale-105`}
                >
                  {/* Icon */}
                  <Icon
                    size={24}
                    className={`${getIconColorClasses(module.color)} mb-1 sm:mb-2 flex-shrink-0 sm:w-8 sm:h-8`}
                  />
                  {/* Name - kort på mobil, full på desktop */}
                  <h3 className="font-semibold text-xs sm:text-sm leading-tight line-clamp-2 break-words">
                    <span className="sm:hidden">{getShortName(module.id)}</span>
                    <span className="hidden sm:inline">{module.name}</span>
                  </h3>
                  {/* Description - skjult på mobil, synlig på desktop */}
                  <p className="hidden sm:block text-xs opacity-70 mt-auto pt-1 line-clamp-2">
                    {module.description}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Fast statusbar i bunnen - kompakt på mobil */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-10">
        <div className="bg-gray-100 px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between text-xs sm:text-sm text-gray-600 max-w-4xl mx-auto">
            <div className="flex items-center space-x-2 sm:space-x-4 overflow-hidden">
              <span className="whitespace-nowrap">{t('dashboard.system')}: <span className="text-green-600 font-medium">{t('dashboard.online')}</span></span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline whitespace-nowrap">{t('dashboard.warehouse')}: {t('dashboard.mainWarehouse')}</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline whitespace-nowrap">{t('dashboard.shift')}: {t('dashboard.dayShift')}</span>
            </div>
            <div className="text-[10px] sm:text-xs whitespace-nowrap">
              {t('dashboard.lastUpdated')}: {new Date().toLocaleTimeString('no-NO')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
